<template>
  <div>
    <div class="page-toolbar">
      <div>
        <div class="page-title">全量控制</div>
        <div class="page-desc">配置保存后立即生效，并在服务重启后保持。</div>
      </div>
      <el-button icon="el-icon-refresh" :loading="loading" @click="loadControls">刷新</el-button>
    </div>

    <div v-loading="loading" class="control-grid">
      <el-card class="page-card control-card">
        <div class="control-header">
          <div>
            <div class="control-title">保证金</div>
            <div class="control-subtitle">控制供应商发布信息前是否需要满足保证金余额。</div>
          </div>
          <el-switch
            :value="depositEnabled"
            active-color="#13ce66"
            active-text="开启"
            inactive-text="关闭"
            :disabled="depositSaving || reviewSaving || loading"
            @change="handleDepositChange"
          />
        </div>
        <el-alert
          class="control-alert"
          :type="depositEnabled ? 'success' : 'info'"
          :closable="false"
          show-icon
          :title="depositEnabled ? '保证金已开启' : '保证金已关闭'"
        >
          <div slot="default">
            {{ depositEnabled
              ? '本次开启时已认证供应商获得豁免，之后新认证供应商需要缴纳保证金。'
              : '所有供应商当前均可绕过保证金余额校验。' }}
          </div>
        </el-alert>
      </el-card>

      <el-card class="page-card control-card">
        <div class="control-header">
          <div>
            <div class="control-title">资质人工审核</div>
            <div class="control-subtitle">仅控制供应商资质，销售和求购信息仍按原流程审核。</div>
          </div>
          <el-switch
            :value="qualificationManualReviewEnabled"
            active-color="#13ce66"
            active-text="人工审核"
            inactive-text="自动通过"
            :disabled="depositSaving || reviewSaving || loading"
            @change="handleQualificationReviewChange"
          />
        </div>
        <el-alert
          class="control-alert"
          :type="qualificationManualReviewEnabled ? 'warning' : 'success'"
          :closable="false"
          show-icon
          :title="qualificationManualReviewEnabled ? '资质提交后等待人工审核' : '资质提交后自动通过'"
        >
          <div slot="default">
            当前待审核资质：<strong>{{ pendingQualificationCount }}</strong> 条
          </div>
        </el-alert>
      </el-card>
    </div>

    <div class="update-meta" v-if="updatedAt">
      最后更新：{{ updatedAt }}<span v-if="updateBy">，操作人：{{ updateBy }}</span>
    </div>
  </div>
</template>

<script>
import {
  fetchGlobalControls,
  updateDepositControl,
  updateQualificationReviewControl
} from '../../api/admin'
import { isAuthRedirectError } from '../../utils/auth'

export default {
  name: 'GlobalControlView',
  data() {
    return {
      loading: false,
      depositSaving: false,
      reviewSaving: false,
      depositEnabled: false,
      qualificationManualReviewEnabled: true,
      pendingQualificationCount: 0,
      updatedAt: '',
      updateBy: ''
    }
  },
  created() {
    this.loadControls()
  },
  methods: {
    applyControl(control) {
      const data = control || {}
      this.depositEnabled = !!data.depositEnabled
      this.qualificationManualReviewEnabled = data.qualificationManualReviewEnabled !== false
      this.pendingQualificationCount = Math.max(0, Number(data.pendingQualificationCount) || 0)
      this.updatedAt = data.updatedAt || ''
      this.updateBy = data.updateBy || ''
    },
    async loadControls() {
      this.loading = true
      try {
        this.applyControl(await fetchGlobalControls())
      } catch (error) {
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '全量控制配置获取失败')
      } finally {
        this.loading = false
      }
    },
    async handleDepositChange(enabled) {
      const previous = !enabled
      if (enabled) {
        try {
          await this.$confirm(
            '当前已认证供应商本轮豁免，之后新认证供应商需要缴纳保证金。',
            '确认开启保证金',
            {
              type: 'warning',
              confirmButtonText: '确认开启',
              cancelButtonText: '取消'
            }
          )
        } catch (error) {
          this.depositEnabled = previous
          return
        }
      }

      this.depositSaving = true
      try {
        const result = await updateDepositControl(enabled)
        this.applyControl(result && result.control)
        const count = Math.max(0, Number(result && result.exemptedSupplierCount) || 0)
        this.$message.success(enabled ? `保证金已开启，本轮豁免 ${count} 名已认证供应商` : '保证金已关闭')
      } catch (error) {
        this.depositEnabled = previous
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '保证金状态更新失败')
      } finally {
        this.depositSaving = false
      }
    },
    async handleQualificationReviewChange(enabled) {
      const previous = !enabled
      if (!enabled && this.pendingQualificationCount > 0) {
        try {
          await this.$confirm(
            `当前有 ${this.pendingQualificationCount} 条待审核资质，关闭后将一并自动通过；如有特殊情况请先处理。`,
            '关闭资质人工审核',
            {
              type: 'warning',
              confirmButtonText: '知道了',
              cancelButtonText: '取消'
            }
          )
        } catch (error) {
          this.qualificationManualReviewEnabled = previous
          return
        }
      }

      this.reviewSaving = true
      try {
        const result = await updateQualificationReviewControl(enabled)
        this.applyControl(result && result.control)
        const count = Math.max(0, Number(result && result.autoApprovedCount) || 0)
        this.$message.success(enabled ? '资质人工审核已开启' : `资质人工审核已关闭，自动通过 ${count} 条资质`)
      } catch (error) {
        this.qualificationManualReviewEnabled = previous
        if (isAuthRedirectError(error)) return
        this.$message.error(error.message || '资质审核状态更新失败')
      } finally {
        this.reviewSaving = false
      }
    }
  }
}
</script>

<style scoped>
.page-desc {
  margin-top: 5px;
  color: #7d88a3;
  font-size: 13px;
}

.control-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.control-card {
  min-height: 220px;
}

.control-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.control-title {
  color: #1d2f57;
  font-size: 18px;
  font-weight: 600;
}

.control-subtitle {
  max-width: 460px;
  margin-top: 8px;
  color: #667188;
  font-size: 13px;
  line-height: 1.7;
}

.control-alert {
  margin-top: 28px;
}

.update-meta {
  margin-top: 14px;
  color: #7d88a3;
  font-size: 12px;
  text-align: right;
}

@media (max-width: 960px) {
  .control-grid {
    grid-template-columns: 1fr;
  }
}
</style>
